import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useGalleryPrefs, type GalleryKind, type GallerySource, type Sensitivity } from '../store/galleryPrefs'
import { useActivityState, type ActivitySubTab } from '../store/activityState'
import { useTabs, type TabSource, type TabSensitivity } from '../store/tabs'
import { useUI } from '../store/ui'
import { useAccounts } from '../store/accounts'
import { useDeletionCheck } from '../store/deletionCheck'
import { useProfileDeletion } from '../store/profileDeletion'
import { db, listAllCached, countCached, getCachedProfileByDid, kvGet } from '../cache/db'
import { COLLECTIONS } from '../api/pull'
import { LikersView } from './LikersView'
import { autoSync } from '../api/autoSync'
import { checkPostsForDeletion } from '../api/checkDeletion'

function formatWindow(hours: number): string {
  if (hours < 24) return `${hours}h`
  if (hours % 168 === 0) return `${hours / 168}w`
  if (hours % 24 === 0) return `${hours / 24}d`
  return `${hours}h`
}

function SectionHead({
  label,
  collapsed,
  onToggle,
  badge,
}: {
  label: string
  collapsed?: boolean
  onToggle?: () => void
  badge?: string
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-1 px-3 pt-3 pb-1 text-left text-[9px] font-bold uppercase tracking-widest text-ink-500 hover:text-ink-300"
    >
      {onToggle ? (
        <span className="mr-0.5 text-[8px]">{collapsed ? '▶' : '▼'}</span>
      ) : null}
      {label}
      {badge ? (
        <span className="ml-auto rounded-full bg-rose-500/20 px-1.5 py-0.5 text-[8px] text-rose-300">{badge}</span>
      ) : null}
    </button>
  )
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-3 pb-2">
      {children}
    </div>
  )
}

function Pill({
  active, onClick, children,
}: {
  active: boolean; onClick: () => void; children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-2 py-0.5 text-[11px] ${
        active
          ? 'border-[color:var(--accent-strong)] bg-white/10 text-white'
          : 'border-white/10 bg-white/5 text-ink-300 hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  )
}

const SENSITIVITY_PILLS_DEF: { key: Sensitivity; label: string }[] = [
  { key: 'all',   label: 'all' },
  { key: 'safe',  label: 'safe' },
  { key: 'adult', label: '18+' },
]

const GALLERY_KINDS_DEF: { k: GalleryKind; label: string }[] = [
  { k: 'img', label: '🖼 img' },
  { k: 'vid', label: '🎬 vid' },
  { k: 'txt', label: '✎ txt' },
]

/**
 * Shared sensitivity / kind / blur / column controls used by BOTH
 * GalleryControlsBody (home) and ProfileGalleryControlsBody (profile).
 * Reads kinds/columns/blur directly from galleryPrefs/ui stores.
 * Sensitivity is passed as a prop because home and profile use different
 * underlying state (activityState.sfw/nsfw vs galleryPrefs.sensitivity).
 */
export function SharedGalleryFilters({
  sensitivity,
  onSensitivity,
  showGalleryControls = true,
  disableDeletedCheck = false,
  profileDeletedLabel,
}: {
  sensitivity: Sensitivity
  onSensitivity: (s: Sensitivity) => void
  showGalleryControls?: boolean
  /** When true the deleted pill only toggles the preference — caller owns the check. */
  disableDeletedCheck?: boolean
  /** Override pill label when disableDeletedCheck is set (shows profile-scoped count). */
  profileDeletedLabel?: string
}) {
  const kinds               = useGalleryPrefs((s) => s.kinds)
  const toggleKind          = useGalleryPrefs((s) => s.toggleKind)
  const columns             = useGalleryPrefs((s) => s.columns)
  const setColumns          = useGalleryPrefs((s) => s.setColumns)
  const fsColumns           = useGalleryPrefs((s) => s.fsColumns)
  const setFsColumns        = useGalleryPrefs((s) => s.setFsColumns)
  const showDeletedOnly     = useGalleryPrefs((s) => s.showDeletedOnly)
  const toggleShowDeletedOnly = useGalleryPrefs((s) => s.toggleShowDeletedOnly)
  const blurAdult           = useUI((s) => s.blurAdult)
  const toggleBlurAdult     = useUI((s) => s.toggleBlurAdult)

  const missingUris    = useDeletionCheck((s) => s.missingUris)
  const lastCheckedAt  = useDeletionCheck((s) => s.lastCheckedAt)
  const checkedOffset  = useDeletionCheck((s) => s.checkedOffset)
  const totalPosts     = useDeletionCheck((s) => s.totalPosts)
  const isChecking     = useDeletionCheck((s) => s.isChecking)
  const startFresh     = useDeletionCheck((s) => s.startFresh)
  const addMissing     = useDeletionCheck((s) => s.addMissing)
  const finalize       = useDeletionCheck((s) => s.finalize)
  const setIsChecking  = useDeletionCheck((s) => s.setIsChecking)
  const setProgress    = useDeletionCheck((s) => s.setProgress)

  const abortRef = useRef<AbortController | null>(null)

  // Cancel in-flight check on unmount (navigation).
  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      abortRef.current = null
    }
  }, [])

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const toggle = (k: string) => setCollapsed((p) => ({ ...p, [k]: !p[k] }))
  const isOpen = (k: string) => !collapsed[k]

  async function handleDeletedPill() {
    if (showDeletedOnly) {
      // Toggle off.
      if (!disableDeletedCheck) {
        abortRef.current?.abort()
        abortRef.current = null
        setIsChecking(false)
      }
      toggleShowDeletedOnly()
      return
    }
    // Turn filter on immediately so tiles reveal as they're found.
    toggleShowDeletedOnly()
    // Profile gallery owns its own check — just toggle, nothing else.
    if (disableDeletedCheck) return
    // Already completed → just show existing results, no new check.
    if (checkedOffset === 0 && lastCheckedAt !== null) return
    // Abort any lingering previous run before starting a new one.
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl
    // Fresh run or resume.
    const isFresh = checkedOffset === 0
    if (isFresh) startFresh()
    setIsChecking(true)
    try {
      const { total } = await checkPostsForDeletion({
        startFromOffset: checkedOffset,
        signal: ctrl.signal,
        onBatch: (newMissing, offset, tot) => {
          if (!ctrl.signal.aborted) addMissing(newMissing, offset, tot)
        },
        onProgress: (checked, tot) => {
          if (!ctrl.signal.aborted) setProgress(checked, tot)
        },
      })
      if (!ctrl.signal.aborted) finalize(total || totalPosts)
    } finally {
      setIsChecking(false)
    }
  }

  const deletedLabel = disableDeletedCheck
    ? (profileDeletedLabel ?? '⊘ deleted')
    : isChecking
      ? totalPosts > 0
        ? `⊘ ${missingUris.length} · ${checkedOffset}/${totalPosts}`
        : 'checking…'
      : missingUris.length > 0
        ? `⊘ ${missingUris.length} deleted`
        : checkedOffset > 0
          ? `⊘ resume (${checkedOffset}/${totalPosts})`
          : '⊘ deleted'

  return (
    <>
      <SectionHead label="sensitivity" collapsed={!isOpen('sens')} onToggle={() => toggle('sens')} />
      {isOpen('sens') && (
        <Row>
          {SENSITIVITY_PILLS_DEF.map((p) => (
            <Pill key={p.key} active={sensitivity === p.key} onClick={() => onSensitivity(p.key)}>
              {p.label}
            </Pill>
          ))}
          <Pill active={showDeletedOnly} onClick={() => { void handleDeletedPill() }}>
            <span className="text-rose-300">{deletedLabel}</span>
          </Pill>
        </Row>
      )}

      {showGalleryControls && (
        <>
          <SectionHead label="content kind" collapsed={!isOpen('kind')} onToggle={() => toggle('kind')} />
          {isOpen('kind') && (
            <>
              <Row>
                {GALLERY_KINDS_DEF.map(({ k, label }) => (
                  <Pill key={k} active={kinds.includes(k)} onClick={() => toggleKind(k)}>
                    {kinds.includes(k) ? '✓ ' : ''}{label}
                  </Pill>
                ))}
              </Row>
              {kinds.length === 0 && (
                <div className="px-3 text-[10px] text-amber-200">no kinds — toggle one on</div>
              )}
            </>
          )}

          <SectionHead label="blur adult" collapsed={!isOpen('blur')} onToggle={() => toggle('blur')} />
          {isOpen('blur') && (
            <Row>
              <Pill active={blurAdult} onClick={toggleBlurAdult}>blur 18+</Pill>
            </Row>
          )}

          <SectionHead label="columns (grid)" collapsed={!isOpen('cols')} onToggle={() => toggle('cols')} />
          {isOpen('cols') && (
            <Row>
              <input
                type="range" min={2} max={12} step={1} value={columns}
                onChange={(e) => setColumns(+e.target.value)}
                className="flex-1 accent-[color:var(--accent-strong)]"
              />
              <span className="w-6 text-right text-xs tabular-nums text-white">{columns}</span>
            </Row>
          )}

          <SectionHead label="columns (fullscreen)" collapsed={!isOpen('fscols')} onToggle={() => toggle('fscols')} />
          {isOpen('fscols') && (
            <Row>
              <input
                type="range" min={3} max={20} step={1} value={fsColumns}
                onChange={(e) => setFsColumns(+e.target.value)}
                className="flex-1 accent-[color:var(--accent-strong)]"
              />
              <span className="w-6 text-right text-xs tabular-nums text-white">{fsColumns}</span>
            </Row>
          )}
        </>
      )}
    </>
  )
}

export function GalleryControlsBody() {
  const groupByUser    = useGalleryPrefs((s) => s.groupByUser)
  const toggleGroup    = useGalleryPrefs((s) => s.toggleGroupByUser)
  const groupBySource  = useGalleryPrefs((s) => s.groupBySource)
  const toggleGroupBySource = useGalleryPrefs((s) => s.toggleGroupBySource)

  const view          = useActivityState((s) => s.view)
  const setView       = useActivityState((s) => s.setView)
  const sfw           = useActivityState((s) => s.sfw)
  const setSfw        = useActivityState((s) => s.setSfw)
  const nsfw          = useActivityState((s) => s.nsfw)
  const setNsfw       = useActivityState((s) => s.setNsfw)
  const subTab        = useActivityState((s) => s.subTab)
  const setSubTab     = useActivityState((s) => s.setSubTab)
  const windowHours   = useActivityState((s) => s.windowHours)
  const setWindowHours = useActivityState((s) => s.setWindowHours)
  const pollSec       = useActivityState((s) => s.pollIntervalSec)
  const setPollSec    = useActivityState((s) => s.setPollIntervalSec)
  const excludedTabs   = useActivityState((s) => s.excludedTabs)
  const toggleTab      = useActivityState((s) => s.toggleTab)
  const selectAllTabs  = useActivityState((s) => s.selectAllTabs)
  const excludeAllTabs = useActivityState((s) => s.excludeAllTabs)
  const coordinatedPairs = useActivityState((s) => s.coordinatedPairs)

  const tabs      = useTabs((s) => s.tabs)
  const closeTab  = useTabs((s) => s.close)
  const navigate  = useNavigate()

  const subTabs: { key: ActivitySubTab; label: string }[] = [
    { key: 'combo',   label: 'all' },
    { key: 'posts',   label: 'posts' },
    { key: 'reposts', label: 'reposts' },
    { key: 'replies', label: 'replies' },
    { key: 'likes',   label: 'likes' },
    { key: 'overlap', label: '⇆ overlap' },
    { key: 'likers',  label: '♡♡ likers' },
  ]

  const [pollInput, setPollInput] = useState(String(pollSec))
  useEffect(() => { setPollInput(String(pollSec)) }, [pollSec])

  // Local collapsed state for each section
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const toggle = (k: string) => setCollapsed((p) => ({ ...p, [k]: !p[k] }))
  const isOpen = (k: string) => !collapsed[k]

  // Aggregate coordinated pairs by (handleA, handleB)
  const pairGroups = (() => {
    const map = new Map<string, { handleA: string; handleB: string; zeroCount: number; subSecCount: number }>()
    for (const p of coordinatedPairs) {
      const key = `${p.handleA}|${p.handleB}`
      const g = map.get(key) ?? { handleA: p.handleA, handleB: p.handleB, zeroCount: 0, subSecCount: 0 }
      if (p.deltaMs === 0) g.zeroCount++
      else g.subSecCount++
      map.set(key, g)
    }
    return Array.from(map.values()).sort((a, b) => (b.zeroCount - a.zeroCount) || (b.subSecCount - a.subSecCount))
  })()

  // Map activityState sfw/nsfw booleans to unified 3-way sensitivity pill.
  // Keeps Home.tsx filtering logic unchanged while giving both panels the same UI.
  const homeSensitivity: Sensitivity = sfw && nsfw ? 'all' : nsfw ? 'adult' : 'safe'
  function handleHomeSensitivity(s: Sensitivity) {
    setSfw(s !== 'adult')
    setNsfw(s !== 'safe')
  }

  return (
    <div className="flex flex-col gap-0 overflow-y-auto border-t border-white/5 bg-[#080e1e] pb-4">

      <SectionHead label="view" collapsed={!isOpen('view')} onToggle={() => toggle('view')} />
      {isOpen('view') && (
        <Row>
          <Pill active={view === 'gallery'} onClick={() => setView('gallery')}>▦ gallery</Pill>
          <Pill active={view === 'list'}    onClick={() => setView('list')}>☰ list</Pill>
          <Pill active={view === 'heatmap'} onClick={() => setView('heatmap')}>⬛ heatmap</Pill>
        </Row>
      )}

      {view === 'gallery' && (
        <>
          <SectionHead label="layout" collapsed={!isOpen('layout')} onToggle={() => toggle('layout')} />
          {isOpen('layout') && (
            <Row>
              <Pill active={!groupByUser} onClick={() => { if (groupByUser) toggleGroup() }}>flat grid</Pill>
              <Pill active={groupByUser} onClick={() => { if (!groupByUser) toggleGroup() }}>by user</Pill>
              {groupByUser && (
                <Pill active={groupBySource} onClick={toggleGroupBySource}>+ by source</Pill>
              )}
            </Row>
          )}
        </>
      )}

      <SharedGalleryFilters
        sensitivity={homeSensitivity}
        onSensitivity={handleHomeSensitivity}
        showGalleryControls={view === 'gallery'}
      />

      <SectionHead label="slice" collapsed={!isOpen('slice')} onToggle={() => toggle('slice')} />
      {isOpen('slice') && (
        <Row>
          {subTabs.map((t) => (
            <Pill key={t.key} active={subTab === t.key} onClick={() => setSubTab(t.key)}>
              {t.label}
            </Pill>
          ))}
        </Row>
      )}

      <SectionHead label="window" collapsed={!isOpen('window')} onToggle={() => toggle('window')} />
      {isOpen('window') && (
        <>
          <Row>
            <input
              type="range" min={1} max={720} step={1} value={windowHours}
              onChange={(e) => setWindowHours(+e.target.value)}
              className="flex-1 accent-[color:var(--accent-strong)]"
            />
            <span className="w-10 text-right text-xs tabular-nums text-white">{formatWindow(windowHours)}</span>
          </Row>
          <Row>
            {[1, 6, 24, 72, 168, 336, 720].map((h) => (
              <Pill key={h} active={windowHours === h} onClick={() => setWindowHours(h)}>
                {formatWindow(h)}
              </Pill>
            ))}
          </Row>
        </>
      )}

      <SectionHead label="poll interval" collapsed={!isOpen('poll')} onToggle={() => toggle('poll')} />
      {isOpen('poll') && (
        <Row>
          <input
            type="number" min={30} max={3600} step={1}
            value={pollInput}
            onChange={(e) => {
              setPollInput(e.target.value)
              const n = parseInt(e.target.value, 10)
              if (!Number.isNaN(n) && n >= 30 && n <= 3600) setPollSec(n)
            }}
            className="w-16 border border-white/10 bg-white/5 px-2 py-0.5 text-right text-xs tabular-nums text-white"
          />
          <span className="text-[11px] text-ink-400">s</span>
          {[30, 60, 300, 900, 3600].map((s) => (
            <Pill key={s} active={pollSec === s} onClick={() => setPollSec(s)}>
              {s < 60 ? `${s}s` : s < 3600 ? `${s / 60}m` : `${s / 3600}h`}
            </Pill>
          ))}
          {pollSec < 60 && (
            <span className="text-[10px] text-amber-200">⚠ &lt;60s</span>
          )}
        </Row>
      )}

      {tabs.length > 0 && (
        <>
          <SectionHead label="watched handles" collapsed={!isOpen('handles')} onToggle={() => toggle('handles')} />
          {isOpen('handles') && (
            <WatchedHandlesList
              tabs={tabs}
              excludedTabs={excludedTabs}
              toggleTab={toggleTab}
              selectAllTabs={selectAllTabs}
              excludeAllTabs={excludeAllTabs}
              closeTab={closeTab}
              navigate={navigate}
            />
          )}
        </>
      )}

      {/* Overlapping Users — coordinated posting detection */}
      <SectionHead
        label="overlapping users"
        collapsed={!isOpen('coordinated')}
        onToggle={() => toggle('coordinated')}
        badge={pairGroups.length > 0 ? String(pairGroups.length) : undefined}
      />
      {isOpen('coordinated') && (
        <div className="px-3 pb-2">
          {pairGroups.length === 0 ? (
            <p className="text-[10px] text-ink-500">no posts within 1s from different watched users yet.</p>
          ) : (
            <>
              <p className="mb-1.5 text-[9px] text-ink-500">
                🟡 &lt;1s apart · 🔴 0ms (impossible for one account)
              </p>
              <div className="flex flex-col gap-1">
                {pairGroups.map((g) => (
                  <div
                    key={`${g.handleA}|${g.handleB}`}
                    className={`rounded border px-2 py-1.5 text-[10px] ${
                      g.zeroCount > 0
                        ? 'border-red-500/50 bg-red-500/10'
                        : 'border-yellow-400/30 bg-yellow-400/5'
                    }`}
                  >
                    <div className="flex items-center gap-1 font-mono">
                      <span className="text-white">@{g.handleA}</span>
                      <span className="text-ink-500">↔</span>
                      <span className="text-white">@{g.handleB}</span>
                    </div>
                    <div className="mt-0.5 flex gap-2 text-[9px]">
                      {g.zeroCount > 0 && (
                        <span className="text-red-400">🔴 {g.zeroCount}× 0ms</span>
                      )}
                      {g.subSecCount > 0 && (
                        <span className="text-yellow-300">🟡 {g.subSecCount}× &lt;1s</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

const SOURCE_PILLS: { key: GallerySource; label: string }[] = [
  { key: 'posts',   label: 'posts' },
  { key: 'replies', label: 'replies' },
  { key: 'reposts', label: 'reposts' },
  { key: 'likes',   label: 'likes' },
]

interface WatchedTab { handle: string; avatar?: string | null }

const TAB_SOURCES: TabSource[] = ['posts', 'replies', 'reposts', 'likes']
const TAB_SENSITIVITIES: TabSensitivity[] = ['inherit', 'all', 'safe', 'adult']

function WatchedHandlesList({
  tabs, excludedTabs, toggleTab, selectAllTabs, excludeAllTabs, closeTab, navigate,
}: {
  tabs: WatchedTab[]
  excludedTabs: string[]
  toggleTab: (h: string) => void
  selectAllTabs: () => void
  excludeAllTabs: (handles: string[]) => void
  closeTab: (h: string) => void
  navigate: (path: string) => void
}) {
  // ALL is a pure flip-flop — state doesn't react to individual toggles.
  // Initial value: true if no one is excluded right now.
  const allOnRef = useRef(excludedTabs.length === 0)
  const [allOn, setAllOn] = useState(allOnRef.current)
  // Per-user override panel (G1/G2): which handle's row is expanded.
  const [cfgOpen, setCfgOpen] = useState<string | null>(null)
  const fullTabs = useTabs((s) => s.tabs)
  const openTab = useTabs((s) => s.open)
  const toggleUserSource = useTabs((s) => s.toggleSource)
  const setUserSensitivity = useTabs((s) => s.setSensitivity)
  const togglePinned = useTabs((s) => s.togglePinned)
  // Per-user disk-archive counters (kv rows written by the archiver).
  const [archCounts, setArchCounts] = useState<Record<string, number>>({})
  useEffect(() => {
    let gone = false
    void (async () => {
      const out: Record<string, number> = {}
      for (const t of fullTabs) {
        const n = await kvGet<number>(`archive:count:${t.handle}`)
        if (n) out[t.handle] = n
      }
      if (!gone) setArchCounts(out)
    })()
    return () => { gone = true }
  }, [fullTabs])
  const globalSources = useGalleryPrefs((s) => s.enabledSources)
  const autoAddFromSearch = useGalleryPrefs((s) => s.autoAddFromSearch)
  const toggleAutoAddFromSearch = useGalleryPrefs((s) => s.toggleAutoAddFromSearch)
  const [addInput, setAddInput] = useState('')

  function handleAll() {
    const next = !allOn
    setAllOn(next)
    if (next) {
      selectAllTabs()
    } else {
      excludeAllTabs(tabs.map((t) => t.handle))
    }
  }

  function addUser() {
    const h = addInput.trim().replace(/^@/, '').toLowerCase()
    if (!h) return
    openTab({ handle: h })
    setAddInput('')
  }

  return (
    <>
      <div className="flex items-center gap-2 px-3 pb-1">
        <button
          type="button"
          onClick={handleAll}
          className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${
            allOn
              ? 'bg-white/10 text-white hover:bg-white/20'
              : 'text-ink-500 hover:bg-white/10 hover:text-white'
          }`}
        >
          ALL
        </button>
        <button
          type="button"
          onClick={toggleAutoAddFromSearch}
          title="when on, opening a search result adds that user here automatically"
          className={`rounded px-2 py-0.5 text-[10px] transition-colors ${
            autoAddFromSearch ? 'bg-white/10 text-white' : 'text-ink-500 hover:text-white'
          }`}
        >
          search auto-add {autoAddFromSearch ? 'on' : 'off'}
        </button>
      </div>
      {/* positive selection: add a user directly, no profile visit needed */}
      <div className="flex items-center gap-1 px-3 pb-1">
        <input
          value={addInput}
          onChange={(e) => setAddInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addUser() }}
          placeholder="+ add @handle to gallery"
          className="min-w-0 flex-1 rounded border border-white/10 bg-white/5 px-2 py-1 text-[11px] text-white placeholder:text-ink-600"
        />
        <button
          type="button"
          onClick={addUser}
          disabled={!addInput.trim()}
          className="shrink-0 rounded border border-emerald-400/40 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-40"
        >
          add
        </button>
      </div>
      <div className="flex flex-col gap-px px-3">
        {[...tabs]
          .sort((a, b) => {
            const pa = fullTabs.find((x) => x.handle === a.handle.toLowerCase())?.pinned ? 0 : 1
            const pb = fullTabs.find((x) => x.handle === b.handle.toLowerCase())?.pinned ? 0 : 1
            return pa - pb
          })
          .map((t) => {
          const included = !excludedTabs.includes(t.handle.toLowerCase())
          const full = fullTabs.find((x) => x.handle === t.handle.toLowerCase())
          const userSources = full?.enabledSources ?? null
          const userSens = full?.sensitivity ?? 'inherit'
          const customized = !!userSources || userSens !== 'inherit'
          const cfg = cfgOpen === t.handle
          return (
            <div key={t.handle} className="flex flex-col">
            <div
              className={`flex items-center gap-1 text-[11px] ${
                included ? 'bg-white/5 text-white' : 'bg-transparent text-ink-500'
              }`}
            >
              {/* pfp → navigate to profile */}
              <button
                type="button"
                onClick={() => navigate(`/profile/${encodeURIComponent(t.handle)}`)}
                className="shrink-0 p-1 hover:opacity-80"
                title={`open @${t.handle}`}
              >
                {t.avatar ? (
                  <img src={t.avatar} alt="" className="h-5 w-5 rounded object-cover" loading="lazy" referrerPolicy="no-referrer" />
                ) : (
                  <span className="block h-5 w-5 rounded bg-sky-500/30" />
                )}
              </button>
              {/* handle → toggle inclusion */}
              <button
                type="button"
                onClick={() => toggleTab(t.handle)}
                className="flex min-w-0 flex-1 items-center gap-1 px-1 py-1 text-left hover:bg-white/10"
              >
                <span className={`truncate ${included ? '' : 'line-through'}`}>
                  @{t.handle}
                </span>
                <span className={`ml-auto shrink-0 text-[9px] ${included ? 'text-emerald-400' : 'text-ink-600'}`}>
                  {included ? '●' : '○'}
                </span>
              </button>
              {/* pin — sorts this user first in list + grouped gallery */}
              <button
                type="button"
                onClick={() => togglePinned(t.handle)}
                title={full?.pinned ? `unpin @${t.handle}` : `pin @${t.handle} first`}
                className={`shrink-0 px-1 py-1 ${full?.pinned ? 'text-amber-300' : 'text-ink-600'} hover:text-amber-200`}
              >
                {full?.pinned ? '★' : '☆'}
              </button>
              {/* per-user source/sensitivity config */}
              <button
                type="button"
                onClick={() => setCfgOpen(cfg ? null : t.handle)}
                title={`per-user filters for @${t.handle}`}
                className={`shrink-0 px-1 py-1 ${customized ? 'text-sky-300' : 'text-ink-600'} hover:text-sky-200`}
              >
                ⚙
              </button>
              {/* close tab */}
              <button
                type="button"
                onClick={() => closeTab(t.handle)}
                title={`remove @${t.handle}`}
                className="shrink-0 px-1.5 py-1 text-ink-600 hover:text-rose-300"
              >
                ✕
              </button>
            </div>
            {cfg && (
              <div className="mb-1 flex flex-col gap-1 rounded-b border border-t-0 border-white/10 bg-white/[0.03] px-2 py-1.5">
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-[9px] uppercase tracking-wide text-ink-500">show</span>
                  {TAB_SOURCES.map((src) => {
                    const active = (userSources ?? globalSources).includes(src)
                    return (
                      <button
                        key={src}
                        type="button"
                        onClick={() => toggleUserSource(t.handle, src, globalSources as TabSource[])}
                        className={`rounded-full border px-1.5 py-0.5 text-[10px] ${
                          active
                            ? 'border-sky-400/50 bg-sky-500/15 text-sky-100'
                            : 'border-white/10 text-ink-500 hover:text-white'
                        }`}
                      >
                        {src}
                      </button>
                    )
                  })}
                </div>
                {archCounts[t.handle.toLowerCase()] ? (
                  <div className="text-[10px] text-ink-500">
                    🗂 {archCounts[t.handle.toLowerCase()].toLocaleString()} archived to disk
                  </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-[9px] uppercase tracking-wide text-ink-500">18+</span>
                  {TAB_SENSITIVITIES.map((sv) => (
                    <button
                      key={sv}
                      type="button"
                      onClick={() => setUserSensitivity(t.handle, sv)}
                      className={`rounded-full border px-1.5 py-0.5 text-[10px] ${
                        userSens === sv
                          ? 'border-amber-400/50 bg-amber-500/15 text-amber-100'
                          : 'border-white/10 text-ink-500 hover:text-white'
                      }`}
                    >
                      {sv === 'inherit' ? 'global' : sv}
                    </button>
                  ))}
                </div>
              </div>
            )}
            </div>
          )
        })}
      </div>
    </>
  )
}

const CONTENT_SOURCES: { key: 'posts' | 'replies' | 'likes' | 'reposts'; label: string }[] = [
  { key: 'posts', label: 'posts' },
  { key: 'replies', label: 'replies' },
  { key: 'likes', label: 'likes' },
  { key: 'reposts', label: 'reposts' },
]

export function ProfileGalleryControlsBody() {
  const profileView         = useUI((s) => s.profileView)
  const setProfileView      = useUI((s) => s.setProfileView)
  const profileSources      = useUI((s) => s.profileSources)
  const profileLikers       = useUI((s) => s.profileLikers)
  const toggleProfileSource = useUI((s) => s.toggleProfileSource)
  const setProfileLikers    = useUI((s) => s.setProfileLikers)
  const sensitivity     = useGalleryPrefs((s) => s.sensitivity)
  const setSensitivity  = useGalleryPrefs((s) => s.setSensitivity)
  const acc             = useAccounts((s) => s.accounts.find((a) => a.id === s.activeId))
  const watchedTabs     = useTabs((s) => s.tabs)
  const navigate        = useNavigate()
  const location        = useLocation()

  const [cachedHandles, setCachedHandles] = useState<string[]>([])
  useEffect(() => {
    void db.pull_manifest.toArray().then((rows) => {
      const handles = [...new Set(rows.map((r) => r.handle.toLowerCase()))].sort()
      setCachedHandles(handles)
    })
  }, [])

  // Current handle from URL. location.pathname keeps percent-encoding and
  // the segment may be a DID (follow lists navigate by did:plc:…), so
  // decode it and resolve DIDs to the canonical handle via the cached
  // profile row. Everything below keys off this value — liker_records,
  // cached counts, nav highlight — and MUST use the same key the profile
  // page reads with (profile.handle), or the caches split-brain.
  const rawSegment = location.pathname.startsWith('/profile/')
    ? (() => {
        const seg = location.pathname.slice('/profile/'.length).split('/')[0]
        try {
          return decodeURIComponent(seg).toLowerCase()
        } catch {
          return seg.toLowerCase()
        }
      })()
    : ''
  const [resolvedDidHandle, setResolvedDidHandle] = useState<string | null>(null)
  useEffect(() => {
    setResolvedDidHandle(null)
    if (!rawSegment.startsWith('did:')) return
    let cancelled = false
    void getCachedProfileByDid(rawSegment)
      .then((row) => {
        if (!cancelled && row?.handle && !row.handle.startsWith('did:')) {
          setResolvedDidHandle(row.handle.toLowerCase())
        }
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [rawSegment])
  const currentHandle = rawSegment.startsWith('did:')
    ? (resolvedDidHandle ?? rawSegment)
    : rawSegment

  // Profile-scoped deletion result for the pill label.
  const profileDeletionResult = useProfileDeletion((s) => s.results[currentHandle] ?? null)
  const profileDeletedLabel = profileDeletionResult
    ? profileDeletionResult.checkedAt === 0
      ? '⊘ checking…'
      : profileDeletionResult.missingUris.length > 0
        ? `⊘ ${profileDeletionResult.missingUris.length} deleted`
        : '⊘ none deleted'
    : undefined

  // Accurate per-source cached counts. Posts and replies share the same
  // db.records collection so we split by the presence of the reply field.
  const [sourceCounts, setSourceCounts] = useState<Record<string, number>>({})
  useEffect(() => {
    if (!currentHandle) return
    let cancelled = false
    void (async () => {
      const allPosts = await listAllCached(currentHandle, COLLECTIONS.posts)
      let posts = 0, replies = 0
      for (const r of allPosts) {
        if ((r.value as { reply?: unknown }).reply) replies++
        else posts++
      }
      const [reposts, likes] = await Promise.all([
        countCached(currentHandle, COLLECTIONS.reposts),
        countCached(currentHandle, COLLECTIONS.likes),
      ])
      if (!cancelled) setSourceCounts({ posts, replies, reposts, likes })
    })()
    return () => { cancelled = true }
  }, [currentHandle])

  // Build deduplicated nav list: self → watched → cached
  const navList = (() => {
    const seen = new Set<string>()
    const list: { handle: string; avatar?: string; label?: string }[] = []
    const add = (handle: string, avatar?: string, label?: string) => {
      const lc = handle.toLowerCase()
      if (!lc || seen.has(lc)) return
      seen.add(lc)
      list.push({ handle: lc, avatar, label })
    }
    if (acc?.handle) add(acc.handle, acc.avatar ?? undefined, 'self')
    for (const t of watchedTabs) add(t.handle, t.avatar ?? undefined, 'watched')
    for (const h of cachedHandles) add(h, undefined, 'cached')
    return list
  })()

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const toggle = (k: string) => setCollapsed((p) => ({ ...p, [k]: !p[k] }))
  const isOpen = (k: string) => !collapsed[k]

  return (
    <div className="flex flex-col gap-0 overflow-y-auto border-t border-white/5 bg-[#080e1e] pb-4">

      <SectionHead label="view" collapsed={!isOpen('view')} onToggle={() => toggle('view')} />
      {isOpen('view') && (
        <Row>
          <Pill active={profileView === 'gallery'} onClick={() => setProfileView('gallery')}>▦ gallery</Pill>
          <Pill active={profileView === 'list'} onClick={() => setProfileView('list')}>☰ feed</Pill>
        </Row>
      )}

      <SectionHead label="content" collapsed={!isOpen('content')} onToggle={() => toggle('content')} />
      {isOpen('content') && (
        <div className="flex flex-col gap-px px-3 pb-2">
          {CONTENT_SOURCES.map((c) => {
            const active = !profileLikers && profileSources.includes(c.key)
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => toggleProfileSource(c.key)}
                className={`flex items-center gap-2 rounded px-2 py-1 text-left text-[11px] ${
                  active ? 'bg-white/10 text-white' : 'text-ink-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                <span className="min-w-0 flex-1">{c.label}</span>
                {sourceCounts[c.key] != null && sourceCounts[c.key] > 0 ? (
                  <span className="shrink-0 text-[9px] text-ink-500 tabular-nums">
                    {sourceCounts[c.key].toLocaleString()}
                  </span>
                ) : null}
                <span className={`shrink-0 text-[9px] ${active ? 'text-emerald-400' : 'text-ink-600'}`}>
                  {active ? '●' : '○'}
                </span>
              </button>
            )
          })}
          {/* Likers — exclusive toggle */}
          <button
            type="button"
            onClick={() => setProfileLikers(!profileLikers)}
            className={`flex items-center gap-2 rounded px-2 py-1 text-left text-[11px] ${
              profileLikers ? 'bg-white/10 text-white' : 'text-ink-300 hover:bg-white/5 hover:text-white'
            }`}
          >
            <span className="min-w-0 flex-1">likers</span>
            <span className={`shrink-0 text-[9px] ${profileLikers ? 'text-emerald-400' : 'text-ink-600'}`}>
              {profileLikers ? '●' : '○'}
            </span>
          </button>
        </div>
      )}

      <SharedGalleryFilters
        sensitivity={sensitivity}
        onSensitivity={setSensitivity}
        showGalleryControls={profileView === 'gallery'}
        disableDeletedCheck
        profileDeletedLabel={profileDeletedLabel}
      />

      <SectionHead label="users" collapsed={!isOpen('users')} onToggle={() => toggle('users')} />
      {isOpen('users') && (
        <div className="flex flex-col gap-px px-3 pb-2">
          {navList.map((u) => {
            const active = u.handle === currentHandle
            return (
              <button
                key={u.handle}
                type="button"
                onClick={() => navigate(`/profile/${encodeURIComponent(u.handle)}`)}
                className={`flex items-center gap-2 rounded px-2 py-1 text-left text-[11px] ${
                  active ? 'bg-white/10 text-white' : 'text-ink-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                {u.avatar ? (
                  <img src={u.avatar} alt="" className="h-5 w-5 shrink-0 rounded object-cover" loading="lazy" referrerPolicy="no-referrer" />
                ) : (
                  <span className="h-5 w-5 shrink-0 rounded bg-sky-500/20" />
                )}
                <span className="min-w-0 flex-1 truncate">@{u.handle}</span>
                {u.label === 'self' ? (
                  <span className="shrink-0 text-[8px] text-sky-400">self</span>
                ) : u.label === 'watched' ? (
                  <span className="shrink-0 text-[8px] text-emerald-400">●</span>
                ) : (
                  <span className="shrink-0 text-[8px] text-ink-600">cache</span>
                )}
              </button>
            )
          })}
          {navList.length === 0 && (
            <p className="text-[10px] text-ink-500">no users yet — open a profile to cache it.</p>
          )}
        </div>
      )}

      {currentHandle && (
        <>
          <SectionHead label="likers" collapsed={!isOpen('likers')} onToggle={() => toggle('likers')} />
          {isOpen('likers') && <LikersView targetHandle={currentHandle} />}
        </>
      )}

      {currentHandle && <SyncCheckSection handle={currentHandle} />}
    </div>
  )
}

function SyncCheckSection({ handle }: { handle: string }) {
  const missingUris   = useDeletionCheck((s) => s.missingUris)
  const lastCheckedAt = useDeletionCheck((s) => s.lastCheckedAt)
  const isChecking    = useDeletionCheck((s) => s.isChecking)
  const checkedOffset = useDeletionCheck((s) => s.checkedOffset)
  const totalPosts    = useDeletionCheck((s) => s.totalPosts)
  const startFresh    = useDeletionCheck((s) => s.startFresh)
  const addMissing    = useDeletionCheck((s) => s.addMissing)
  const finalize      = useDeletionCheck((s) => s.finalize)
  const setIsChecking = useDeletionCheck((s) => s.setIsChecking)
  const setProgress   = useDeletionCheck((s) => s.setProgress)
  const acc = useAccounts((s) => s.accounts.find((a) => a.id === s.activeId))

  const [phase, setPhase] = useState<string | null>(null)
  const [summary, setSummary] = useState<string | null>(null)
  const syncAbortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    return () => { syncAbortRef.current?.abort() }
  }, [])

  async function run() {
    if (!acc || isChecking) return
    syncAbortRef.current?.abort()
    const ctrl = new AbortController()
    syncAbortRef.current = ctrl
    setPhase('syncing…')
    setSummary(null)
    try {
      await autoSync({ targetHandle: handle, targetDid: acc.did, signal: ctrl.signal })
      if (ctrl.signal.aborted) return
      setPhase('checking deletions…')
      // Always a full fresh check from the sidebar button.
      startFresh()
      setIsChecking(true)
      const { missing, total } = await checkPostsForDeletion({
        signal: ctrl.signal,
        onBatch: (newMissing, offset, tot) => {
          if (!ctrl.signal.aborted) addMissing(newMissing, offset, tot)
        },
        onProgress: (checked, tot) => {
          if (!ctrl.signal.aborted) setProgress(checked, tot)
        },
      })
      if (!ctrl.signal.aborted) {
        finalize(total)
        setSummary(
          missing.length > 0
            ? `⊘ ${missing.length} deleted of ${total} checked`
            : `✓ all ${total} posts present`,
        )
      }
    } catch (e) {
      if (!ctrl.signal.aborted) setSummary(`error: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setIsChecking(false)
      setPhase(null)
    }
  }

  const [collapsed, setCollapsed] = useState(false)

  return (
    <>
      <SectionHead
        label="sync + check"
        collapsed={collapsed}
        onToggle={() => setCollapsed((v) => !v)}
        badge={missingUris.length > 0 ? String(missingUris.length) : undefined}
      />
      {!collapsed && (
        <div className="flex flex-col gap-2 px-3 pb-3">
          <button
            type="button"
            className="glass-button text-xs w-full text-left"
            style={{ borderColor: 'var(--accent-strong)', color: 'var(--accent-strong)' }}
            disabled={isChecking}
            onClick={() => void run()}
          >
            {isChecking ? phase ?? 'working…' : '⟳ sync + check deletions'}
          </button>
          {isChecking && totalPosts > 0 && (
            <div className="h-1 w-full rounded bg-ink-700">
              <div
                className="h-full rounded bg-[color:var(--accent-strong)] transition-all"
                style={{ width: `${Math.round((checkedOffset / totalPosts) * 100)}%` }}
              />
            </div>
          )}
          {isChecking && totalPosts > 0 && (
            <p className="text-[10px] text-ink-400">
              {checkedOffset}/{totalPosts} checked · {missingUris.length} found
            </p>
          )}
          {summary && (
            <p className={`text-[10px] ${summary.startsWith('⊘') ? 'text-amber-400' : summary.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>
              {summary}
            </p>
          )}
          {!isChecking && lastCheckedAt && !summary && (
            <p className="text-[10px] text-ink-500">
              last check: {new Date(lastCheckedAt).toLocaleDateString()} · {missingUris.length} flagged
            </p>
          )}
        </div>
      )}
    </>
  )
}
